import React, { useState, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { Upload, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfileSettings = () => {
  const { vendor } = useAuth();
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    business_name: vendor?.business_name || '',
    email: vendor?.email || '',
    whatsapp: vendor?.whatsapp || '',
    store_description: vendor?.store_description || ''
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [upiId, setUpiId] = useState(vendor?.upi_id || '');
  const [paymentQr, setPaymentQr] = useState(vendor?.payment_qr_url || '');
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [qrUploading, setQrUploading] = useState(false);
  const qrFileRef = useRef(null);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    const digits = (profileData.whatsapp || '').replace(/\D/g, '');
    if (digits.length < 10) {
      toast.error('Please enter a valid WhatsApp number — order alerts are sent there.');
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('vendor_token');
      await axios.put(`${API}/vendor/profile`, profileData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Profile updated successfully!');
      // Reload page to update context
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleQrUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image.'); return; }
    setQrUploading(true);
    try {
      const token = localStorage.getItem('vendor_token');
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API}/upload/image`, form, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setPaymentQr(res.data.url);
      toast.success('QR image uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setQrUploading(false);
    }
  };

  const handlePaymentSave = async () => {
    setPaymentLoading(true);
    try {
      const token = localStorage.getItem('vendor_token');
      await axios.put(`${API}/vendor/profile`, { upi_id: upiId, payment_qr_url: paymentQr }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Payment settings saved!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setPaymentLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('vendor_token');
      await axios.post(
        `${API}/vendor/change-password`,
        {
          current_password: passwordData.current_password,
          new_password: passwordData.new_password,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Password changed successfully!');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="profile-settings-page">
      <div className="mb-8">
        <h2 className="text-3xl font-bold tracking-tight">Profile Settings</h2>
        <p className="text-muted-foreground mt-2">Manage your account information and security</p>
      </div>

      <div className="space-y-6">
        {/* Profile Information */}
        <Card>
          <CardHeader>
            <CardTitle>Profile Information</CardTitle>
            <CardDescription>Update your business details and store description</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleProfileUpdate} className="space-y-4">
              {/* WhatsApp number — primary requirement for order alerts */}
              <div className="space-y-2 rounded-lg border border-emerald-200 bg-emerald-50/60 p-3">
                <Label htmlFor="whatsapp" className="flex items-center gap-2">
                  <svg viewBox="0 0 32 32" className="h-4 w-4 fill-[#25D366]" aria-hidden><path d="M16 3C9.4 3 4 8.4 4 15c0 2.1.6 4.1 1.6 5.9L4 29l8.3-1.6c1.7.9 3.6 1.4 5.7 1.4 6.6 0 12-5.4 12-12S22.6 3 16 3zm5.6 14.4c-.3-.2-1.8-.9-2.1-1s-.5-.2-.7.2-.8 1-1 1.2-.4.3-.7.1c-1.8-.9-3-1.6-4.2-3.6-.3-.5.3-.5.8-1.5.1-.2 0-.4 0-.6s-.7-1.7-1-2.3c-.3-.6-.5-.5-.7-.5h-.6c-.2 0-.6.1-.9.4-1.2 1.2-1.1 2.9-.2 4.4 1.7 2.6 3.5 4.2 6.5 5.4 2.6 1 3 .8 3.6.7.7-.1 1.8-.7 2-1.5.3-.7.3-1.4.2-1.5z"/></svg>
                  WhatsApp number <span className="text-red-600">*</span>
                </Label>
                <Input
                  id="whatsapp"
                  type="tel"
                  value={profileData.whatsapp}
                  onChange={(e) => setProfileData({ ...profileData, whatsapp: e.target.value })}
                  placeholder="e.g. 9779001200"
                  data-testid="whatsapp-input"
                  required
                />
                <p className="text-xs text-emerald-800">
                  New orders are sent here on WhatsApp. Include country code (default +91 if you enter a 10-digit number).
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="business_name">Business Name</Label>
                <Input
                  id="business_name"
                  value={profileData.business_name}
                  onChange={(e) => setProfileData({ ...profileData, business_name: e.target.value })}
                  data-testid="business-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={profileData.email}
                  onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                  data-testid="email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="store_description">Store Description</Label>
                <Textarea
                  id="store_description"
                  value={profileData.store_description}
                  onChange={(e) => setProfileData({ ...profileData, store_description: e.target.value })}
                  placeholder="Brief description of your store that will appear on your storefront"
                  rows={3}
                  data-testid="store-description-input"
                />
                <p className="text-xs text-muted-foreground">
                  This description will be displayed below your store name on your public storefront
                </p>
              </div>
              <Button type="submit" disabled={loading} data-testid="update-profile-btn">
                {loading ? 'Updating...' : 'Update Profile'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Payment Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Payment Settings</CardTitle>
            <CardDescription>Let customers pay online via UPI before confirming their order</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="upi_id">UPI ID</Label>
              <Input
                id="upi_id"
                value={upiId}
                onChange={(e) => setUpiId(e.target.value)}
                placeholder="yourname@upi"
                data-testid="upi-id-input"
              />
              <p className="text-xs text-muted-foreground">Shown to customers before checkout — tap to copy on mobile.</p>
            </div>

            <div className="space-y-2">
              <Label>Payment QR Code</Label>
              {paymentQr ? (
                <div className="relative w-40">
                  <img src={paymentQr} alt="Payment QR" className="w-40 h-40 object-contain rounded-xl border" />
                  <button
                    type="button"
                    onClick={() => setPaymentQr('')}
                    className="absolute -top-2 -right-2 rounded-full bg-destructive text-destructive-foreground h-6 w-6 flex items-center justify-center shadow"
                    aria-label="Remove QR"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ) : (
                <>
                  <input ref={qrFileRef} type="file" accept="image/*" className="hidden" onChange={handleQrUpload} />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => qrFileRef.current?.click()}
                    disabled={qrUploading}
                    data-testid="upload-qr-btn"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {qrUploading ? 'Uploading…' : 'Upload QR Image'}
                  </Button>
                </>
              )}
              <p className="text-xs text-muted-foreground">Upload your UPI / bank QR code so customers can scan and pay.</p>
            </div>

            <Button onClick={handlePaymentSave} disabled={paymentLoading} data-testid="save-payment-btn">
              {paymentLoading ? 'Saving…' : 'Save Payment Settings'}
            </Button>
          </CardContent>
        </Card>

        {/* Password Change */}
        <Card>
          <CardHeader>
            <CardTitle>Change Password</CardTitle>
            <CardDescription>Update your account password</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="current_password">Current Password</Label>
                <Input
                  id="current_password"
                  type="password"
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                  data-testid="current-password-input"
                  required
                />
              </div>
              <Separator />
              <div className="space-y-2">
                <Label htmlFor="new_password">New Password</Label>
                <Input
                  id="new_password"
                  type="password"
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  data-testid="new-password-input"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm_password">Confirm New Password</Label>
                <Input
                  id="confirm_password"
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                  data-testid="confirm-password-input"
                  required
                />
              </div>
              <Button type="submit" disabled={loading} variant="destructive" data-testid="change-password-btn">
                {loading ? 'Changing...' : 'Change Password'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProfileSettings;